// category-hue — the kit's ONE gate onto the nine categorization hues
// (DEV-GUIDE-002 §12.5; base.css two-tone pairs: `bg-<hue>-tint` 100 ground +
// `text-<hue>-ink` 900 content, both themes; `text-<hue>-ink-band` the 1000
// stop for the gray band).
//
// Promoted from modules/lib/src/fe/category-hue.tsx on 2026-09-17 (audit C-04,
// L8, COL-06). The PAIR table had been copied four times (lib, stat-card,
// avatar-photo, list-view/stats), and avatar-photo's name hash differed from
// lib's by CASE, so the same person could wear two hues on two screens. Fin,
// cms, audit and the three portal homes could not import lib's copy at all
// (Fig 4.1: module → module is a banned edge), so each hand-wrote the identity
// capsule with the same comment text. One table, one hash, one capsule now.
//
// Categorization ONLY (§12.5 "Never"): these mark WHAT a thing is — a
// console's identity, a subject, a file format, a locale — always beside (or
// as) a text label. They never signal state: status keeps StatusBadge and the
// semantic roles (§12.4), and nothing here may leak into a
// success / warning / error surface.
//
// Class strings are COMPLETE per hue (house rule: never assembled from
// fragments — Tailwind must see each literal utility).
//
// THE NAMES AND THE HASH LIVE IN @erp/core-schemas (2026-09-19, batch 17,
// decision D2): a stored category colour is now one of these nine names, so
// `modules/lib/schemas` and `modules/cms/schemas` validate the wire with the
// same tuple this file paints from — and a schemas lane may not import the
// kit. `CategoryHue`, `HUES` and `hueForName` are re-exported here unchanged
// for every screen that already imports them from this path. The print-side
// literals (STATIC_900, STATIC_LIGHT) sit in ./static.ts, JSX-free, published
// as `@erp/ui/category-hue/static` for the node-lane print generators.
import type { Component } from "solid-js";
import { Dynamic } from "solid-js/web";
import { CATEGORY_HUES, hueForName } from "@erp/core-schemas";
import type { CategoryHue } from "./static.js";

export type { CategoryHue };
export { hueForName };
export { STATIC_900, STATIC_LIGHT } from "./static.js";

/** Canonical order, from base.css (the `@erp/core-schemas` tuple). APPEND-ONLY:
 *  hueForName indexes into this array, so reordering or inserting re-colors
 *  every hashed chip in the wild. */
export const HUES: readonly CategoryHue[] = CATEGORY_HUES;

/**
 * The repo-wide MODULE IDENTITY MAP (AGENTS.md, DEV-GUIDE-002 §12.5) — stable,
 * do not reinvent or extend ad hoc. Keys are the product areas a portal shows
 * side by side; the value is the hue that marks that area's capsules, chips
 * and category KPIs on every screen.
 */
export const MODULE_HUE = {
  catalogue: "indigo",
  circulation: "cyan",
  acquisitions: "seafoam",
  patrons: "magenta",
  digital: "purple",
  finance: "celery",
  cms: "fuchsia",
  audit: "yellow",
  operations: "chartreuse",
  reports: "chartreuse",
} as const satisfies Record<string, CategoryHue>;

/** The hue's display name — a swatch's accessible label, a table cell that names a stored hue. */
export const HUE_LABEL: Record<CategoryHue, string> = {
  indigo: "Indigo",
  purple: "Purple",
  fuchsia: "Fuchsia",
  magenta: "Magenta",
  yellow: "Yellow",
  celery: "Celery",
  chartreuse: "Chartreuse",
  cyan: "Cyan",
  seafoam: "Seafoam",
};

/** One hue's three complete utilities: the opaque 100 ground, the 900 ink,
 *  and the 1000 ink for the gray band (§12.5 "On the gray band"). */
export interface HuePair {
  /** `bg-<hue>-tint` — the opaque hue-100 ground. */
  tint: string;
  /** `text-<hue>-ink` — the hue-900 content colour, ≥ 4.65:1 on its tint. */
  ink: string;
  /** `text-<hue>-ink-band` — the hue-1000 stop, for text on base-300 (the band), where 900 measures under 4.5. */
  inkBand: string;
}

/** The two-tone utilities per hue (base.css `@theme inline`). */
export const PAIR: Record<CategoryHue, HuePair> = {
  indigo: { tint: "bg-indigo-tint", ink: "text-indigo-ink", inkBand: "text-indigo-ink-band" },
  purple: { tint: "bg-purple-tint", ink: "text-purple-ink", inkBand: "text-purple-ink-band" },
  fuchsia: { tint: "bg-fuchsia-tint", ink: "text-fuchsia-ink", inkBand: "text-fuchsia-ink-band" },
  magenta: { tint: "bg-magenta-tint", ink: "text-magenta-ink", inkBand: "text-magenta-ink-band" },
  yellow: { tint: "bg-yellow-tint", ink: "text-yellow-ink", inkBand: "text-yellow-ink-band" },
  celery: { tint: "bg-celery-tint", ink: "text-celery-ink", inkBand: "text-celery-ink-band" },
  chartreuse: { tint: "bg-chartreuse-tint", ink: "text-chartreuse-ink", inkBand: "text-chartreuse-ink-band" },
  cyan: { tint: "bg-cyan-tint", ink: "text-cyan-ink", inkBand: "text-cyan-ink-band" },
  seafoam: { tint: "bg-seafoam-tint", ink: "text-seafoam-ink", inkBand: "text-seafoam-ink-band" },
};

/* An unmapped identity keeps the UI's gray body — a neutral capsule beside
   colored ones is honest ("this area has no assigned hue"), never wrong.
   base-200 is the layer-1 ground and gray-700 its text floor (§12.2). */
const NEUTRAL_PAIR = "bg-base-200 text-base-muted";

/** The complete `bg-<hue>-tint text-<hue>-ink` pair for a surface the
 *  components below do not cover (an initials tile, a stat chip). "neutral"
 *  gives the gray body for the unmarked half of a pair. */
export function huePair(hue: CategoryHue | "neutral"): string {
  return hue === "neutral" ? NEUTRAL_PAIR : `${PAIR[hue].tint} ${PAIR[hue].ink}`;
}

/** Ink-only class — for a mark that takes its shape from the caller (a
 *  `bg-current` identity dot beside its label). Ink is a CONTENT colour, so the
 *  dot inherits it through currentColor rather than anyone writing
 *  `bg-<hue>-ink` as a surface. */
export function hueInk(hue: CategoryHue): string {
  return PAIR[hue].ink;
}

/**
 * The chip shape every category chip shares — `Tag` (in ../tag) builds on the
 * same two strings, so a CategoryChip and a Tag sit level in one row.
 * s = 20px (daisyUI badge-sm; the table-cell chip, StatusBadge's own height);
 * m = 24px (Spectrum Badge S / component-height-75; the form-chip height).
 * Radius is rounded-selector = corner-radius-500 = 8px, Spectrum's badge corner.
 */
export const CHIP_SIZE = {
  s: "inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-selector px-1.5 text-xs font-medium",
  m: "inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-selector px-2 text-xs font-medium",
} as const;

/** Chip sizes: `s` 20px (table cells, beside a StatusBadge), `m` 24px (form chips). */
export type ChipSize = keyof typeof CHIP_SIZE;

/**
 * A category chip: the two-tone label itself (the chip IS the text — no icon,
 * no dot, nothing status-shaped). For a labelled code, a removable chip or a
 * neutral default reach for `Tag` instead.
 */
export function CategoryChip(props: {
  /** The category's name — the chip's whole content. */
  label: string;
  /** The hue. Defaults to `hueForName(label)`, so a subject is the same colour on every screen without a registry; "neutral" keeps the gray body for the unmarked half of a pair (Print beside eBook). */
  hue?: CategoryHue | "neutral";
  /** `s` (20px, the default) in table cells; `m` (24px) beside form controls. */
  size?: ChipSize;
  /** Automation id on the chip. */
  id?: string;
}) {
  return (
    <span
      id={props.id}
      class={`${CHIP_SIZE[props.size ?? "s"]} ${
        props.hue === "neutral" ? NEUTRAL_PAIR : huePair(props.hue ?? hueForName(props.label))
      }`}
    >
      {props.label}
    </span>
  );
}

/* Complete size classes. The capsule owns its glyph size so a capsule of a
   given size looks the same on every screen: s = 32px / 16 (a console tile),
   m = 36px / 16 (a card header, a quick-action tile), l = 40px / 20 (beside
   the page h1). */
const CAPSULE = {
  s: { box: "size-8", glyph: 16 },
  m: { box: "size-9", glyph: 16 },
  l: { box: "size-10", glyph: 20 },
} as const;

/** Capsule sizes: `s` 32px, `m` 36px, `l` 40px. */
export type CapsuleSize = keyof typeof CAPSULE;

/**
 * An identity icon capsule: a rounded-field square on the tint ground with a
 * lucide glyph inheriting the ink through currentColor. It marks WHICH module
 * or area a surface belongs to (`hue={MODULE_HUE.finance}`) and never a state.
 * Decorative by contract — it sits beside visible text (a heading, a tile
 * label), so it is aria-hidden unless `label` names it.
 */
export function IconCapsule(props: {
  /** The area's hue from `MODULE_HUE`; "neutral" for an area the map has not assigned. */
  hue: CategoryHue | "neutral";
  /** A lucide-solid icon COMPONENT (`BookOpen`, not `<BookOpen/>`) — the capsule sets its size. */
  icon: Component<{ size?: number; class?: string }>;
  /** `s` 32px (console tiles), `m` 36px (card headers, the default), `l` 40px (beside the page h1). */
  size?: CapsuleSize;
  /** An accessible name — ONLY where the capsule is the sole identity mark (a dense cell). Beside visible text leave it off: the capsule is then hidden from AT. */
  label?: string;
}) {
  const s = () => CAPSULE[props.size ?? "m"];
  return (
    <span
      class={`grid shrink-0 place-items-center rounded-field ${s().box} ${
        props.hue === "neutral" ? NEUTRAL_PAIR : huePair(props.hue)
      }`}
      role={props.label ? "img" : undefined}
      aria-label={props.label}
      aria-hidden={props.label ? undefined : "true"}
    >
      <Dynamic component={props.icon} size={s().glyph} aria-hidden="true" />
    </span>
  );
}
